/**
 * Public speaker portraits bundled with the site.
 *
 * This catalog is shared by public pages and authenticated admin surfaces so a
 * speaker does not have one identity on the website and a different, empty
 * avatar in the CRM. These are committed public assets, never member source
 * photos or unpublished generation candidates.
 */
const BUNDLED_SPEAKER_CATALOG = [
  ["Nahed Khairallah", "stop-answering-same-questions-ai-hr-assistant", "/media/speakers/nahed-khairallah-cutout.png"],
  ["Michael Goldberg", "scaling-hiring-infrastructure-growth", "/media/speakers/michael-goldberg-cutout.png"],
  ["Farrah Mitra", "hard-conversations-every-leader-needs", "/media/speakers/farrah-mitra-cutout.png"],
  ["Tiona Nicholas", "the-human-advantage-building-community-age-ai", "/media/speakers/tiona-nicholas-cutout.png"],
  ["Dr. Diane Dye", "evidence-based-decision-model", "/media/speakers/diane-dye-cutout.png"],
  ["Meshell R. Baker", "the-secret-to-everyday-confidence", "/media/speakers/meshell-baker-portrait.webp"],
  ["Johnathan Tebeau", "advancing-hr-capabilities-with-ai", "/media/speakers/johnathan-tebeau-cutout.png"],
  ["Sylvester Williams", "", "/media/speakers/sylvester-williams.webp"],
  ["Seth Bauman", "", "/media/speakers/seth-bauman.webp"],
] as const;

export const BUNDLED_SPEAKER_PORTRAITS: Readonly<Record<string, string>> =
  Object.fromEntries(BUNDLED_SPEAKER_CATALOG.map(([name, , path]) => [name, path]));

export const BUNDLED_SPEAKER_PORTRAITS_BY_EVENT: Readonly<Record<string, string>> =
  Object.fromEntries(BUNDLED_SPEAKER_CATALOG.map(([, eventSlug, path]) => [eventSlug, path]));

export const MULTIPLE_SPEAKERS_PORTRAIT = "/media/speakers/multi-speaker-default.png";

const GROUP_SPEAKER_NAME = /\b(multiple|panel|speakers|various|team|guests|tbd|tba)\b|\s(?:&|and|\+)\s|;/i;

export function isMultipleSpeakerName(name: string) {
  return GROUP_SPEAKER_NAME.test(String(name ?? ""));
}

/** Names enter through both catalog data and editable account records. */
export function speakerNameKey(name: string) {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

const BUNDLED_BY_NAME_KEY = new Map(
  Object.entries(BUNDLED_SPEAKER_PORTRAITS).map(([name, src]) => [speakerNameKey(name), src]),
);

/** Returns only a safe, public site asset; it never resolves a private source photo. */
export function bundledSpeakerPortraitForName(name: string): string | null {
  if (isMultipleSpeakerName(name)) return MULTIPLE_SPEAKERS_PORTRAIT;
  return BUNDLED_BY_NAME_KEY.get(speakerNameKey(name)) ?? null;
}

/**
 * An event assignment is a stronger person-to-site link than editable display
 * text, which may omit titles or middle initials.
 */
export function bundledSpeakerPortraitForEvents(eventSlugs: readonly string[]): string | null {
  for (const eventSlug of eventSlugs) {
    const portrait = BUNDLED_SPEAKER_PORTRAITS_BY_EVENT[eventSlug];
    if (portrait) return portrait;
  }
  return null;
}